import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';
import { AppNotification } from '../services/notificationService';

// ── Snackbar (toast) API ────────────────────────────────────────────────────

interface ToastNotification {
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor, duration?: number) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  // Persistent notification bell state
  persistentNotifications: AppNotification[];
  setPersistentNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within a NotificationProvider');
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastNotification | null>(null);
  const [open, setOpen] = useState(false);
  const [persistentNotifications, setPersistentNotifications] = useState<AppNotification[]>([]);

  const showNotification = useCallback((
    message: string,
    severity: AlertColor = 'info',
    duration = 6000,
  ) => {
    setToast({ message, severity, duration });
    setOpen(true);
  }, []);

  const showSuccess = useCallback((m: string) => showNotification(m, 'success'), [showNotification]);
  const showError   = useCallback((m: string) => showNotification(m, 'error'),   [showNotification]);
  const showInfo    = useCallback((m: string) => showNotification(m, 'info'),    [showNotification]);
  const showWarning = useCallback((m: string) => showNotification(m, 'warning'), [showNotification]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <NotificationContext.Provider
      value={{ showNotification, showSuccess, showError, showInfo, showWarning, persistentNotifications, setPersistentNotifications }}
    >
      {children}
      <Snackbar
        open={open}
        autoHideDuration={toast?.duration ?? 6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={toast?.severity ?? 'info'} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
